import React, { useState, useEffect } from "react";
import CountUpWrapper from "../../CountUpWrapper";

const POINTS_TABLE = {
  phone:   { New: 50, Used: 30, Damaged: 10 },
  laptop:  { New: 100, Used: 70, Damaged: 25 },
  tablet:  { New: 80, Used: 55, Damaged: 20 },
  monitor: { New: 60, Used: 40, Damaged: 15 },
  printer: { New: 40, Used: 25, Damaged: 8  },
  other:   { New: 30, Used: 20, Damaged: 5  },
};

const DashboardSummary = ({ user }) => {
  const [stats, setStats] = useState({ points: 0 });
  const [deviceType, setDeviceType] = useState("");
  const [deviceCondition, setDeviceCondition] = useState("");
  const [estimatedPoints, setEstimatedPoints] = useState(null);

  useEffect(() => {
    if (!user || !user.recyclingHistory) return;
    const totalPoints = user.recyclingHistory.reduce((acc, entry) => acc + (entry.pointsEarned || 0), 0);
    setStats({ points: totalPoints });
  }, [user]);

  useEffect(() => {
    if (deviceType && deviceCondition) {
      const pts = POINTS_TABLE[deviceType]?.[deviceCondition] ?? 0;
      setEstimatedPoints(pts);
    } else {
      setEstimatedPoints(null);
    }
  }, [deviceType, deviceCondition]);

  const isNewUser = !user?.recyclingHistory?.length;

  const totalItems = (user?.recyclingHistory || []).reduce((sum, e) => sum + (e.itemsRecycled || 1), 0);

  const deviceBreakdown = (user?.recyclingHistory || []).reduce((acc, e) => {
    const key = (e.deviceType || "other").toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const topDevices = Object.entries(deviceBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 3);

  return (
    <div className="grid font-Inter grid-cols-1 lg:grid-cols-3 gap-4 px-4 md:px-6 mx-auto my-auto lg:space-x-2">

      {/* Total Points Earned */}
      <div className="bg-white w-full max-w-xs md:max-w-sm pt-4 pb-4 rounded-3xl shadow-md flex flex-col items-center justify-start relative overflow-hidden mx-auto" data-aos="fade-left" data-aos-delay="200">
        <h3 className="text-xs font-semibold text-[#29752B] mb-2 py-1 tracking-widest uppercase px-4">Total Points Earned</h3>
        <div className="w-full h-px bg-[#BDBDBD] mb-4"></div>
        <span className="text-[80px] md:text-[100px] md:mx-2 text-[#172A3A]" style={{ fontVariantNumeric: "tabular-nums" }}>
          <CountUpWrapper start={0} end={stats.points} duration={5} redraw={true} />
        </span>
        <span className="text-lg text-[#29752B] font-medium -mt-2">pts</span>
        <p className="text-xs text-gray-400 mt-3 px-4 text-center">
          {totalItems > 0 ? `From ${totalItems} item${totalItems !== 1 ? "s" : ""} recycled` : "Start recycling to earn points!"}
        </p>
      </div>

      {/* Profile Impact */}
      <div className="bg-white w-full max-w-xs md:max-w-sm pt-4 pb-4 rounded-3xl shadow-md flex flex-col items-center justify-start relative overflow-hidden mx-auto" data-aos="fade-left" data-aos-delay="300">
        <div className="flex items-center justify-between w-full px-4 py-1">
          <h3 className="text-xs font-semibold text-[#29752B] mb-2 tracking-widest uppercase">Profile Impact</h3>
          <div className="bg-[#FEC731] text-white text-xs mb-1 w-5 h-5 rounded-full flex items-center justify-center font-bold" title="Your recycling breakdown by device type">i</div>
        </div>
        <div className="w-full h-px bg-[#BDBDBD] mb-4"></div>

        {isNewUser ? (
          <div className="flex flex-col items-center text-center px-4">
            <div className="text-4xl mb-3">♻️</div>
            <p className="text-[#6C757D] text-xs mb-4 leading-relaxed">
              Start recycling your old devices to see your impact here. Every item makes a difference!
            </p>
            <button
              className="flex items-center bg-[#398F3E] text-white px-4 py-2 rounded-full text-sm hover:bg-[#206A29] transition"
              onClick={() => { window.location.href = "/map"; }}
            >
              <img src="/images/plug_icon.png" alt="Recycle" className="w-4 h-4 mr-2" />
              Recycle Now
            </button>
          </div>
        ) : (
          <div className="w-full px-4">
            <div className="space-y-2">
              {topDevices.map(([device, count], i) => {
                const barColors = ["bg-[#236823]", "bg-[#4BA86D]", "bg-[#59CBAC]"];
                const pct = Math.round((count / totalItems) * 100);
                return (
                  <div key={device}>
                    <div className="flex justify-between text-xs text-[#414042] mb-1">
                      <span className="capitalize">{device}</span>
                      <span>{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${barColors[i]} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center">{totalItems} total item{totalItems !== 1 ? "s" : ""} recycled</p>
          </div>
        )}
      </div>

      {/* Reward Point Estimate Calculator */}
      <div className="bg-white w-full max-w-xs md:max-w-sm pt-4 pb-4 rounded-3xl shadow-md flex flex-col items-start justify-start mx-auto" data-aos="fade-left" data-aos-delay="400">
        <div className="w-full">
          <h3 className="text-xs font-semibold text-[#29752B] px-4 pb-1 tracking-widest uppercase">Point Estimate Calculator</h3>
          <p className="text-[#414042] text-[10px] mb-2 px-4">See how many points your device is worth.</p>
          <div className="w-full h-px bg-[#BDBDBD] mb-3"></div>
        </div>

        <div className="px-4 w-full space-y-3">
          <div>
            <label htmlFor="deviceType" className="block text-[#414042] text-xs font-medium mb-1">Device Type</label>
            <select
              id="deviceType"
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#29752B] text-xs"
            >
              <option value="">Select device…</option>
              <option value="phone">Phone</option>
              <option value="laptop">Laptop</option>
              <option value="tablet">Tablet</option>
              <option value="monitor">Monitor</option>
              <option value="printer">Printer</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="deviceCondition" className="block text-[#414042] text-xs font-medium mb-1">Condition</label>
            <select
              id="deviceCondition"
              value={deviceCondition}
              onChange={(e) => setDeviceCondition(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#29752B] text-xs"
            >
              <option value="">Select condition…</option>
              <option value="New">New / Working</option>
              <option value="Used">Used / Some wear</option>
              <option value="Damaged">Damaged / Broken</option>
            </select>
          </div>

          <div className="bg-[#EEFAF7] rounded-xl px-3 py-3 mt-1">
            <p className="text-xs text-[#344054] font-medium mb-1">Estimated reward:</p>
            {estimatedPoints !== null ? (
              <p className="text-2xl font-bold text-[#18421B]">
                ~{estimatedPoints} <span className="text-sm font-medium">pts</span>
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic">Select device and condition above</p>
            )}
            <p className="text-[9px] text-[#FF6666] mt-2">
              *Estimate only. Final offer confirmed by collection centre.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSummary;
